/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { DotMark } from '@carbon/react/icons';
import { Tooltip } from 'carbon-components-react';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Voice } from '../../../../../ts/text-to-speech';
import { capitalizeFirstLetter } from '../../../../ia/util/string';
import {
  getTTSModelLanguageDescription,
  getVoiceName,
  isModelExpressive
} from '../../../helpers/text-helpers';
import { TtsAvatar } from '../../icons/TtsAvatar';
import { TextToSpeech as messages } from './messages';

interface TextToSpeechVoiceTileProps {
  voiceModel: Voice;
  // The following props are mainly used by 11Labs Visualizer section
  expressive?: Boolean;
  modelName?: string;
  voiceName?: string;
}

/**
 * This renders a tile with information about the current selected
 * text-to-speech voice. It includes the name of the voice formatted
 * in a friendly way with its corresponding gender and information
 * about expressive voices if it is expressive
 */
export const TextToSpeechVoiceTile = (props: TextToSpeechVoiceTileProps) => {
  const { voiceModel, expressive = true, modelName, voiceName } = props;
  const intl = useIntl();
  return (
    <>
      <div className="TextToSpeech__avatar-container">
        <TtsAvatar gender={voiceModel?.gender} />
        <div className="TextToSpeech__voice-container">
          <div className="TextToSpeech__voice-name">
            {voiceName ? voiceName : getVoiceName(voiceModel?.friendly_name)}
          </div>

          <div className="TextToSpeech__voice-description">
            <div className="TextToSpeech__voice-dot">
              {modelName && <> {modelName} </>}
              {voiceModel?.language && (
                <>
                  <DotMark size={16} />
                  {getTTSModelLanguageDescription(voiceModel?.language)}
                </>
              )}
            </div>
            {voiceModel?.gender && (
              <div className="TextToSpeech__voice-dot">
                <DotMark size={16} />
                {capitalizeFirstLetter(voiceModel.gender)}
              </div>
            )}
            {expressive &&
              voiceModel?.friendly_name &&
              isModelExpressive(voiceModel?.friendly_name) && (
                <>
                  <div className="TextToSpeech__voice-dot">
                    <DotMark size={16} />
                  </div>
                  <FormattedMessage {...messages.expressiveText} />
                  <Tooltip
                    direction="bottom"
                    iconDescription={intl.formatMessage(
                      messages.moreInformation
                    )}
                    key="information-tooltip-1"
                    tabIndex={0}>
                    {isModelExpressive(voiceModel.friendly_name) ? (
                      <FormattedMessage {...messages.expressiveModelTooltip} />
                    ) : (
                      <FormattedMessage {...messages.expressiveModelTooltip} />
                    )}
                  </Tooltip>
                </>
              )}
          </div>
        </div>
      </div>
    </>
  );
};
